import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useSupplyStore } from "../store/supplyStore";
import OrderDetailRow from "./OrderDetailRow";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;

  @media (min-width: 768px) {
    gap: 24px;
    padding: 24px;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.05);
  border: 2px solid #609f94;
  border-radius: 16px;
  padding: 16px 20px;
  transition: all 0.3s ease;

  &:focus-within {
    border-color: #24b97d;
    background-color: rgba(255, 255, 255, 0.08);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 16px;
  font-family: "Ubuntu", sans-serif;

  &::placeholder {
    color: #8ca9a9;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FilterLabel = styled.div`
  color: #8ca9a9;
  font-size: 14px;
  font-weight: 500;
`;

const StatusFilters = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (min-width: 768px) {
    gap: 12px;
  }
`;

const StatusButton = styled.button<{ $isActive: boolean; $status: string }>`
  padding: 10px 16px;
  border: 2px solid ${props => 
    props.$status === 'pending' ? '#F59E0B' :
    props.$status === 'confirmed' ? '#10B981' :
    props.$status === 'shipped' ? '#3B82F6' :
    props.$status === 'delivered' ? '#059669' :
    '#EF4444'
  };
  border-radius: 20px;
  background-color: ${props => props.$isActive ? 
    (props.$status === 'pending' ? '#F59E0B' :
     props.$status === 'confirmed' ? '#10B981' :
     props.$status === 'shipped' ? '#3B82F6' :
     props.$status === 'delivered' ? '#059669' :
     '#EF4444') : 'transparent'
  };
  color: ${props => props.$isActive ? '#FFFFFF' : 
    (props.$status === 'pending' ? '#F59E0B' :
     props.$status === 'confirmed' ? '#10B981' :
     props.$status === 'shipped' ? '#3B82F6' :
     props.$status === 'delivered' ? '#059669' :
     '#EF4444')
  };
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 44px;
  white-space: nowrap;

  @media (min-width: 768px) {
    padding: 8px 16px;
    min-height: auto;
  }

  &:hover {
    background-color: ${props => 
      props.$status === 'pending' ? 'rgba(245, 158, 11, 0.2)' :
      props.$status === 'confirmed' ? 'rgba(16, 185, 129, 0.2)' :
      props.$status === 'shipped' ? 'rgba(59, 130, 246, 0.2)' :
      props.$status === 'delivered' ? 'rgba(5, 150, 105, 0.2)' :
      'rgba(239, 68, 68, 0.2)'
    };
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ClearFiltersButton = styled.button`
  align-self: flex-end;
  background: none;
  border: none;
  color: #24b97d;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  padding: 8px;
  min-height: 44px;
  display: flex;
  align-items: center;

  @media (min-width: 768px) {
    padding: 0;
    min-height: auto;
  }

  &:hover {
    color: #1ea56a;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const OrdersTable = styled.div`
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 16px;
  overflow: hidden;
  overflow-x: auto;
  min-width: 100%;
  
  /* Стили для скроллбара */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #609f94;
    border-radius: 4px;
    
    &:hover {
      background: #24b97d;
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileOrdersList = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const OrderCard = styled.div<{ $isExpanded?: boolean }>`
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
    border-color: rgba(36, 185, 125, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const CardOrderId = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #24b97d;
`;

const CardExpandButton = styled.button<{ $isExpanded: boolean }>`
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: #8ca9a9;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};

  &:hover {
    background: rgba(36, 185, 125, 0.2);
    color: #24b97d;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`;

const CardLabel = styled.span`
  color: #8ca9a9;
  font-weight: 500;
`;

const CardValue = styled.span`
  color: #ffffff;
  font-weight: 500;
`;

const CardPrice = styled.span`
  color: #24b97d;
  font-weight: 600;
  font-size: 16px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 1fr) 120px 120px 100px 120px 40px;
  gap: 16px;
  padding: 16px 20px;
  background-color: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 14px;
  font-weight: 500;
  color: #8ca9a9;
  min-width: 720px;

  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 12px;
    gap: 8px;
    grid-template-columns: minmax(180px, 1fr) 80px 90px 80px 100px 32px;
    min-width: 640px;
  }
`;

const OrderRow = styled.div<{ $isExpanded?: boolean }>`
  display: grid;
  grid-template-columns: minmax(200px, 1fr) 120px 120px 100px 120px 40px;
  gap: 16px;
  padding: 20px;
  border-bottom: ${props => props.$isExpanded ? 'none' : '1px solid rgba(255, 255, 255, 0.05)'};
  transition: background-color 0.2s ease;
  cursor: pointer;
  min-width: 720px;

  @media (max-width: 768px) {
    padding: 12px 16px;
    gap: 8px;
    grid-template-columns: minmax(180px, 1fr) 80px 90px 80px 100px 32px;
    min-width: 640px;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.02);
  }

  &:last-child {
    border-bottom: ${props => props.$isExpanded ? 'none' : 'none'};
  }
`;

const ExpandButton = styled.button<{ $isExpanded: boolean }>`
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  color: #8ca9a9;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, color 0.2s ease;
  transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};

  @media (min-width: 768px) {
    width: 24px;
    height: 24px;
  }

  &:hover {
    color: #24b97d;
  }

  svg {
    width: 14px;
    height: 14px;

    @media (min-width: 768px) {
      width: 16px;
      height: 16px;
    }
  }
`;

const OrderCell = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #ffffff;
  min-width: 0;
  word-break: break-word;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const OrderId = styled.div`
  font-weight: 500;
  color: #24b97d;
`;

const StatusBadge = styled.div<{ $status: string }>`
  padding: 3px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
  background-color: ${props => 
    props.$status === 'pending' ? 'rgba(245, 158, 11, 0.2)' :
    props.$status === 'confirmed' ? 'rgba(16, 185, 129, 0.2)' :
    props.$status === 'shipped' ? 'rgba(59, 130, 246, 0.2)' :
    props.$status === 'delivered' ? 'rgba(5, 150, 105, 0.2)' :
    'rgba(239, 68, 68, 0.2)'
  };
  color: ${props => 
    props.$status === 'pending' ? '#F59E0B' :
    props.$status === 'confirmed' ? '#10B981' :
    props.$status === 'shipped' ? '#3B82F6' :
    props.$status === 'delivered' ? '#059669' :
    '#EF4444'
  };
  text-align: center;
  white-space: nowrap;

  @media (min-width: 768px) {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
  }
`;

const PriceCell = styled(OrderCell)`
  font-weight: 600;
  color: #24b97d;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  gap: 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const EmptyDescription = styled.p`
  font-size: 16px;
  color: #8ca9a9;
  margin: 0;
  max-width: 400px;
`;

const ScrollHint = styled.div`
  display: block;
  padding: 8px 16px;
  background-color: rgba(36, 185, 125, 0.1);
  border: 1px solid rgba(36, 185, 125, 0.2);
  border-radius: 8px;
  color: #24b97d;
  font-size: 12px;
  text-align: center;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;

const LoadMoreButton = styled.button`
  background: linear-gradient(135deg, #24BA7D 0%, #1E9B69 100%);
  border: none;
  border-radius: 16px;
  color: #FFFFFF;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 16px;
  padding: 16px 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #1E9B69 0%, #188A5A 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(36, 186, 125, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 14px 24px;
    min-height: 48px;
  }
`;

const statusLabels = {
  pending: 'В ожидании',
  confirmed: 'Подтвержден',
  shipped: 'Отправлен',
  delivered: 'Доставлен',
  cancelled: 'Отменен'
};

const SupplyOrdersHistory: React.FC = () => {
  const {
    orders,
    isLoading,
    isLoadingMore,
    hasMoreOrders,
    ordersSearchQuery,
    ordersStatusFilter,
    fetchOrders,
    loadMoreOrders,
    setOrdersSearchQuery,
    setOrdersStatusFilter,
    getFilteredOrders
  } = useSupplyStore();

  const [localSearchQuery, setLocalSearchQuery] = useState(ordersSearchQuery);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setOrdersSearchQuery(localSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [localSearchQuery, setOrdersSearchQuery]);

  const filteredOrders = getFilteredOrders();

  const handleStatusFilter = (status: string) => {
    const newFilters = ordersStatusFilter.includes(status)
      ? ordersStatusFilter.filter(s => s !== status)
      : [...ordersStatusFilter, status];
    
    setOrdersStatusFilter(newFilters);
  };

  const clearFilters = () => {
    setOrdersStatusFilter([]);
    setLocalSearchQuery('');
  };

  const toggleOrderExpansion = (orderId: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <Container>
        <EmptyState>
          <EmptyTitle>Загрузка заказов...</EmptyTitle>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Найти нужный заказ"
          value={localSearchQuery}
          onChange={(e) => setLocalSearchQuery(e.target.value)}
        />
      </SearchContainer>

      <FiltersContainer>
        <FilterLabel>Фильтр по статусу</FilterLabel>
        <StatusFilters>
          {Object.entries(statusLabels).map(([status, label]) => (
            <StatusButton
              key={status}
              $isActive={ordersStatusFilter.includes(status)}
              $status={status}
              onClick={() => handleStatusFilter(status)}
            >
              {label}
            </StatusButton>
          ))}
          {(ordersStatusFilter.length > 0 || localSearchQuery) && (
            <ClearFiltersButton onClick={clearFilters}>
              Очистить фильтры
            </ClearFiltersButton>
          )}
        </StatusFilters>
      </FiltersContainer>

      <OrdersTable>
        <TableHeader>
          <div>Номер заказа</div>
          <div>Кол-во</div>
          <div>Сумма</div>
          <div>Статус</div>
          <div>Дата</div>
          <div></div>
        </TableHeader>
        
        {filteredOrders.length > 0 ? (
          filteredOrders.map((order) => (
            <React.Fragment key={order.id}>
              <OrderRow 
                $isExpanded={expandedOrders.has(order.id)}
                onClick={() => toggleOrderExpansion(order.id)}
              >
                <OrderCell>
                  <OrderId>{order.id}</OrderId>
                </OrderCell>
                <OrderCell>{order.totalItems || order.quantity}</OrderCell>
                <PriceCell>{formatPrice(order.totalAmount ?? (order.price * (order.totalItems || order.quantity)))}</PriceCell>
                <OrderCell>
                  <StatusBadge $status={order.status}>
                    {statusLabels[order.status]}
                  </StatusBadge>
                </OrderCell>
                <OrderCell>{formatDate(order.createdAt)}</OrderCell>
                <OrderCell>
                  <ExpandButton $isExpanded={expandedOrders.has(order.id)}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                  </ExpandButton>
                </OrderCell>
              </OrderRow>
              {expandedOrders.has(order.id) && (
                <OrderDetailRow order={order} />
              )}
            </React.Fragment>
          ))
        ) : (
          <EmptyState>
            <EmptyTitle>Заказы не найдены</EmptyTitle>
            <EmptyDescription>
              {ordersSearchQuery || ordersStatusFilter.length > 0
                ? "По вашему запросу заказы не найдены. Попробуйте изменить параметры поиска."
                : "У вас пока нет заказов на закупку товаров."}
            </EmptyDescription>
          </EmptyState>
        )}
      </OrdersTable>

      <MobileOrdersList>
        {filteredOrders.length > 0 ? (
          filteredOrders.map((order) => (
            <React.Fragment key={order.id}>
              <OrderCard 
                $isExpanded={expandedOrders.has(order.id)}
                onClick={() => toggleOrderExpansion(order.id)}
              >
                <CardHeader>
                  <CardOrderId>{order.id}</CardOrderId>
                  <CardExpandButton $isExpanded={expandedOrders.has(order.id)}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                  </CardExpandButton>
                </CardHeader>
                
                <CardContent>
                  <CardRow>
                    <CardLabel>Количество товаров</CardLabel>
                    <CardValue>{order.totalItems || order.quantity} шт.</CardValue>
                  </CardRow>
                  
                  <CardRow>
                    <CardLabel>Общая сумма</CardLabel>
                    <CardPrice>{formatPrice(order.totalAmount ?? (order.price * (order.totalItems || order.quantity)))}</CardPrice>
                  </CardRow>
                  
                  <CardRow>
                    <CardLabel>Статус</CardLabel>
                    <StatusBadge $status={order.status}>
                      {statusLabels[order.status]}
                    </StatusBadge>
                  </CardRow>
                  
                  <CardRow>
                    <CardLabel>Дата создания</CardLabel>
                    <CardValue>{formatDate(order.createdAt)}</CardValue>
                  </CardRow>
                </CardContent>
              </OrderCard>
              
              {expandedOrders.has(order.id) && (
                <OrderDetailRow order={order} />
              )}
            </React.Fragment>
          ))
        ) : (
          <EmptyState>
            <EmptyTitle>Заказы не найдены</EmptyTitle>
            <EmptyDescription>
              {ordersSearchQuery || ordersStatusFilter.length > 0
                ? "По вашему запросу заказы не найдены. Попробуйте изменить параметры поиска."
                : "У вас пока нет заказов на закупку товаров."}
            </EmptyDescription>
          </EmptyState>
        )}
      </MobileOrdersList>

      {/* Кнопка "Загрузить ещё" */}
      {filteredOrders.length > 0 && hasMoreOrders && (
        <LoadMoreContainer>
          <LoadMoreButton 
            onClick={loadMoreOrders} 
            disabled={isLoadingMore}
          >
            {isLoadingMore ? "Загрузка..." : "Загрузить ещё"}
          </LoadMoreButton>
        </LoadMoreContainer>
      )}
    </Container>
  );
};

export default SupplyOrdersHistory;
